import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SharedService } from '@shared/services/shared.service';
import { IAllegation } from 'app/models/compliance/allegation';
import { ComplaintService } from 'app/services/compliance/complaint.service';

@Component({
  selector: 'app-allegation',
  templateUrl: './allegation.component.html',
  styleUrl: './allegation.component.scss'
})
export class AllegationComponent implements OnInit, OnChanges {
  @Input() complaintId: number = 0;

  public allegations: IAllegation[] = [];
  public allegation: IAllegation =
  {
    id: 0,
    complaintId: 0,
    allegation: '',
    details: '',
    archived: false
  };
  public allegationOptions: string[] = ['Placeholder Allegation'];
  public addingAllegation: boolean = false;
  public allegationColumns: string[] = ['allegation', 'details', 'archived', 'actions'];
  public includeArchived: boolean = false;
  public allegationSource = new MatTableDataSource<IAllegation>(this.allegations);
  public allegationForm = new FormGroup({
    allegation: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    details: new FormControl('', { nonNullable: true, validators: [Validators.required] })
  });

  @ViewChild('allegationPaginator', { static: false }) allegationPaginator!: MatPaginator;
  @ViewChild('allegationSort', { static: false }) allegationSort!: MatSort;

  public sharedService = inject(SharedService);
  public complaintService = inject(ComplaintService);

  ngOnInit(): void {
    this.loadAllegations();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['complaintId'] && !changes['complaintId'].firstChange) {
      this.closeAllegation();
      this.loadAllegations();
    }
  }

  public loadAllegations(includeArchived: boolean = false): void {
    if (!this.complaintId) {
      this.allegations = [];
      this.updateAllegationTable();
      return;
    }

    this.complaintService.getAllegationsByComplaint(this.complaintId, includeArchived).subscribe({
      next: (response: IAllegation[]) => this.allegations = response,
      error: error => {
        this.sharedService.toastr.error('Could not load allegations');
        console.log('error loading allegations', error);
      },
      complete: () => this.updateAllegationTable()
    });
  }

  toggleArchived(includeArchived: boolean){
    this.includeArchived = includeArchived;
    this.loadAllegations(includeArchived);
  }

  public toggleAllegationForm(adding: boolean): void {
    this.addingAllegation = adding;
    if (adding) {
      this.resetAllegationForm();
      return;
    }

    this.closeAllegation();
  }

  public selectAllegation(allegation: IAllegation): void {
    this.addingAllegation = true;
    this.allegation = allegation;
    this.allegationForm.patchValue({
      allegation: allegation.allegation,
      details: allegation.details
    });
  }

  public saveAllegation(): void {
    if (!this.complaintId || this.allegationForm.invalid) {
      return;
    }

    if (this.allegation.id === 0) {
      this.createAllegation();
      return;
    }

    this.updateAllegation();
  }

  public createAllegation(): void {
    let allegation: IAllegation = this.getAllegationFromForm();

    this.complaintService.createAllegation(allegation).subscribe({
      next: (response: IAllegation) => this.allegations.push(response),
      error: error => {
        this.sharedService.toastr.error('Could not create allegation');
        console.log('error creating allegation', error);
      },
      complete: () => {
        this.closeAllegation();
        this.updateAllegationTable();
      }
    });
  }

  public updateAllegation(): void {
    this.allegation = this.getAllegationFromForm();

    this.complaintService.updateAllegation(this.allegation).subscribe({
      next: (response: IAllegation) => {
        let index: number = this.allegations.findIndex(a => a.id === response.id);
        if (index > -1) {
          this.allegations[index] = response;
        }
      },
      error: error => {
        this.sharedService.toastr.error('Could not update allegation');
        console.log('error updating allegation', error);
      },
      complete: () => {
        this.closeAllegation();
        this.updateAllegationTable();
      }
    });
  }

  public archiveAllegation(allegation: IAllegation, archive: boolean): void {
    this.sharedService.openConfirm(archive ? 'Archive this allegation?' : 'Unarchive this allegation?');
    this.sharedService.confirmed().subscribe(confirmed => {
      if (confirmed) {
        this.complaintService.archiveAllegation(allegation.id, archive).subscribe({
          complete: () => {
            allegation.archived = archive;
            if(archive){
              this.allegations = this.allegations.filter(a => a.id !== allegation.id);
            }
            this.updateAllegationTable();
          },
          error: error => {
            this.sharedService.toastr.error('Could not ' + (archive ? 'archive' : 'unarchive') + ' allegation');
            console.log('error ' + (archive ? 'archiving' : 'unarchiving') + ' allegation', error);
          }
        });
      }
    });
  }

  public closeAllegation(): void {
    this.addingAllegation = false;
    this.allegation = {
      id: 0,
      complaintId: 0,
      allegation: '',
      details: '',
      archived: false
    };
    this.resetAllegationForm();
    this.updateAllegationTable();
  }

  private updateAllegationTable(): void {
    setTimeout(() => {
      this.allegationSource.data = this.allegations;
      this.allegationSource.paginator = this.allegationPaginator;
      this.allegationSource.sort = this.allegationSort;
    }, 500);
  }

  private resetAllegationForm(): void {
    this.allegationForm.reset({
      allegation: '',
      details: ''
    });
  }

  private getAllegationFromForm(): IAllegation {
    return {
      id: this.allegation.id,
      complaintId: this.complaintId,
      allegation: this.allegationForm.controls.allegation.value,
      details: this.allegationForm.controls.details.value,
      archived: false
    };
  }
}
